import type { PerUsdOn } from './displaySeries';

/**
 * Intraday candles for the coin page chart (1H and 4H), from CoinGecko's OHLC
 * endpoint. Pure, so the bucketing and the time handling can be tested.
 *
 * ── Why /ohlc, and what it gives ─────────────────────────────────────────────
 *
 * `/coins/{id}/ohlc?interval=hourly&days=90` returns REAL hourly candles (true
 * open / high / low / close per hour) on paid plans, for up to 90 days. That is
 * the source for 1H, and 4H is aggregated from it. `market_chart` was the other
 * option and is worse for this: it returns one price SNAPSHOT per hour, so a 1H
 * candle built from it would be a flat line, and its volume is a rolling 24-hour
 * total, not the volume traded in that hour.
 *
 * The OHLC endpoint has NO volume, so intraday views have no volume bars.
 *
 * Without hourly access (the keyless public API used in local dev rejects
 * `interval=hourly`), `days=30` with no interval returns CoinGecko's own 4-hour
 * candles. That still gives a real 4H chart for 30 days; only 1H is unavailable.
 */

/** Chart intervals, in button order. */
export type CandleInterval = 'hourly' | '4h' | 'daily' | 'weekly';

const INTERVALS: readonly CandleInterval[] = ['hourly', '4h', 'daily', 'weekly'];

/**
 * Any stored or passed-in interval, as one the chart supports. Anything else,
 * including the retired 'monthly', falls back to 1D.
 */
export function normalizeInterval(value: unknown): CandleInterval {
  return INTERVALS.includes(value as CandleInterval) ? (value as CandleInterval) : 'daily';
}

export const isIntraday = (i: CandleInterval) => i === 'hourly' || i === '4h';

export const HOUR_MS = 3_600_000;
export const FOUR_HOURS_MS = 4 * HOUR_MS;

/** One candle, keyed by its OPEN time in UTC milliseconds. */
export interface OhlcBar {
  openMs: number;
  open: number;
  high: number;
  low: number;
  close: number;
  /** Absent for CoinGecko OHLC data, which carries no volume. */
  volume?: number;
}

/** Median spacing between rows, in ms. Used to tell hourly data from 4-hourly. */
export function medianStepMs(rows: readonly (readonly number[])[]): number {
  if (rows.length < 2) return 0;
  const gaps = rows.slice(1).map((r, i) => r[0] - rows[i][0]).sort((a, b) => a - b);
  return gaps[gaps.length >> 1];
}

/** Fold `b` into `a`, `b` being the later bar. */
function mergeInto(a: OhlcBar, b: OhlcBar): void {
  a.high = Math.max(a.high, b.high);
  a.low = Math.min(a.low, b.low);
  a.close = b.close;
  if (b.volume !== undefined) a.volume = (a.volume ?? 0) + b.volume;
}

/**
 * Parse `[closeMs, open, high, low, close]` rows into bars keyed by OPEN time.
 *
 * CoinGecko stamps each candle with its CLOSE time. Charts plot a candle at its
 * open, so each is moved back into the step it covers: floor((close - 1) / step).
 * The `- 1` puts a candle closing exactly on 08:00 in the 07:00 slot, and the
 * still-forming latest candle (closing at, say, 08:12) in the 08:00 slot. Two
 * rows landing in one slot are merged rather than duplicated. Malformed rows are
 * dropped.
 */
export function parseOhlcRows(rows: unknown, stepMs: number): OhlcBar[] {
  if (!Array.isArray(rows)) return [];
  const bySlot = new Map<number, OhlcBar>();
  const valid = rows
    .filter((r): r is number[] =>
      Array.isArray(r) && r.length >= 5 && r.slice(0, 5).every((v) => typeof v === 'number' && isFinite(v)))
    .sort((a, b) => a[0] - b[0]);
  for (const [closeMs, open, high, low, close] of valid) {
    const openMs = Math.floor((closeMs - 1) / stepMs) * stepMs;
    const bar: OhlcBar = { openMs, open, high, low, close };
    const existing = bySlot.get(openMs);
    if (existing) mergeInto(existing, bar);
    else bySlot.set(openMs, bar);
  }
  return Array.from(bySlot.values()).sort((a, b) => a.openMs - b.openMs);
}

/**
 * Hourly bars into 4-hour bars, on UTC-aligned boundaries (00:00, 04:00, 08:00,
 * ...), the same alignment CoinGecko's own 4-hour candles and most exchanges use.
 *
 * Real OHLC, not gapless: open is the first hour's open, close the last hour's
 * close, high and low the extremes across the bucket. Volume is summed when the
 * hours carry any (CoinGecko OHLC does not), and left absent otherwise.
 */
export function aggregateTo4h(hourly: readonly OhlcBar[]): OhlcBar[] {
  const buckets = new Map<number, OhlcBar>();
  const ordered = [...hourly].sort((a, b) => a.openMs - b.openMs);
  for (const h of ordered) {
    const openMs = Math.floor(h.openMs / FOUR_HOURS_MS) * FOUR_HOURS_MS;
    const b = buckets.get(openMs);
    if (!b) buckets.set(openMs, { ...h, openMs });
    else mergeInto(b, h);
  }
  return Array.from(buckets.values()).sort((a, b) => a.openMs - b.openMs);
}

const utcDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/**
 * Bars in USD into the display currency, each at its own day's rate (today's at
 * live spot, via chartRates), so the newest candle uses the same rate as the
 * header price. Volume, being money too, is converted alongside.
 */
export function convertBars(bars: readonly OhlcBar[], rates: PerUsdOn): OhlcBar[] {
  return bars.map((b) => {
    const r = rates.perUsdOn(utcDay(b.openMs));
    const out: OhlcBar = { openMs: b.openMs, open: b.open * r, high: b.high * r, low: b.low * r, close: b.close * r };
    if (b.volume !== undefined) out.volume = b.volume * r;
    return out;
  });
}

/**
 * The browser's local wall-clock time for a UTC instant, re-expressed as if it
 * were UTC, in SECONDS. Lightweight Charts formats every time as UTC, so this
 * shift is how its axis and crosshair read in the user's local time (the
 * approach the library's own docs recommend).
 */
export function localWallClockSec(utcMs: number): number {
  const d = new Date(utcMs);
  return Date.UTC(
    d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()
  ) / 1000;
}

/**
 * Bars with their chart time in local wall-clock seconds, strictly ascending.
 *
 * Strictly ascending matters: when daylight saving ends, one local hour happens
 * twice, two hourly bars map to the same local time, and Lightweight Charts
 * throws on non-ascending data. Such a collision is merged into one bar.
 * `toChartSec` is injectable so tests can simulate a DST change without
 * depending on the machine's timezone.
 */
export function toLocalChartBars(
  bars: readonly OhlcBar[],
  toChartSec: (utcMs: number) => number = localWallClockSec,
): (OhlcBar & { time: number })[] {
  const out: (OhlcBar & { time: number })[] = [];
  for (const b of bars) {
    const time = toChartSec(b.openMs);
    const prev = out[out.length - 1];
    if (prev && time <= prev.time) mergeInto(prev, b);
    else out.push({ ...b, time });
  }
  return out;
}

// ── Display currency ───────────────────────────────────────────────────────────

/**
 * The app's display currencies CoinGecko can price in directly. For these the
 * coin chart fetches candles in the display currency itself (vs_currency) and
 * applies no FX conversion at all: CoinGecko's own series are converted at its
 * own continuous rates, so there is no day where one rate source hands over to
 * another. Anything not listed falls back to USD candles converted per day.
 */
const COINGECKO_VS = new Set(['USD', 'AUD', 'EUR', 'GBP', 'JPY', 'CAD']);

/** CoinGecko `vs_currency` for a display currency, or null if it has none. */
export function coingeckoVsCurrency(code: string): string | null {
  const c = (code || '').toUpperCase();
  return COINGECKO_VS.has(c) ? c.toLowerCase() : null;
}

/** A candle as the chart plots it: time in chart seconds. */
export interface ChartCandle {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
}

/**
 * Close the newest candle at the live price the page header shows, when that
 * candle is the one still forming.
 *
 * The header is the live spot price; the chart's newest close is the last point
 * of a CoinGecko series fetched a little earlier (and, for daily data, cached at
 * the edge for minutes). Pinning makes the two read the same. High and low are
 * widened if the live price is outside them, so the candle stays valid.
 *
 * Only a CURRENT candle is pinned: one whose period contains `nowChartSec`
 * (expressed in the same time basis as the candles). If the data stops short of
 * now, pinning would graft today's price onto an old candle, so it is left alone.
 */
export function pinLastClose<T extends ChartCandle>(
  candles: readonly T[],
  livePrice: number,
  periodSec: number,
  nowChartSec: number,
): T[] {
  const last = candles[candles.length - 1];
  if (!last || !(livePrice > 0) || !isFinite(livePrice)) return candles as T[];
  const current = nowChartSec >= last.time && nowChartSec < last.time + periodSec;
  if (!current) return candles as T[];
  const pinned = {
    ...last,
    close: livePrice,
    high: Math.max(last.high, livePrice),
    low: Math.min(last.low, livePrice),
  };
  return [...candles.slice(0, -1), pinned];
}

// ── Session cache ──────────────────────────────────────────────────────────────

/**
 * One request per key per session: the PROMISE is cached, so concurrent callers
 * share one in-flight request, and a failure evicts itself so a retry can go
 * again. `fresh` forces a refetch (the Retry button).
 */
export function createSessionCache<T>(load: (key: string) => Promise<T>) {
  const cache = new Map<string, Promise<T>>();
  return {
    get(key: string, fresh = false): Promise<T> {
      if (fresh) cache.delete(key);
      let p = cache.get(key);
      if (!p) {
        p = load(key);
        cache.set(key, p);
        p.catch(() => { if (cache.get(key) === p) cache.delete(key); });
      }
      return p;
    },
    has: (key: string) => cache.has(key),
  };
}

// ── Display polish ─────────────────────────────────────────────────────────────

/**
 * Continuous candles: each candle opens at the previous candle's close.
 *
 * CoinGecko's OHLC is built from sampled prices, so a candle's open rarely
 * equals the previous close and the chart shows hairline gaps between bodies.
 * Chaining the opens closes them, as exchange charts do. High and low are
 * widened where the new open falls outside them, so every candle stays valid.
 * The first candle has no predecessor and is left alone. A DISPLAY step only:
 * run it after currency handling and aggregation, never on stored data.
 */
export function chainOpens<T extends ChartCandle>(candles: readonly T[]): T[] {
  return candles.map((c, i) => {
    if (i === 0) return c;
    const open = candles[i - 1].close;
    return { ...c, open, high: Math.max(c.high, open), low: Math.min(c.low, open) };
  });
}

/** How far CoinGecko may lag before a forming candle is no longer drawn. */
export const MAX_LIVE_LAG_PERIODS = 3;

/**
 * The newest candle, live.
 *
 *   - Newest candle IS the current period: close it on the live price
 *     (pinLastClose).
 *   - Newest candle's period has ENDED (CoinGecko lags): append a forming candle
 *     for the current period, opening at the previous close and closing at the
 *     live price, high / low spanning the two. Its time is the newest candle's
 *     time plus whole periods, so it lands on the same grid as the rest (the
 *     4H grid is UTC-aligned, which a plain floor of local time would not be).
 *   - More than MAX_LIVE_LAG_PERIODS behind: nothing. Joining today's price
 *     straight onto weeks-old data would draw a move that never happened.
 *
 * No live price, or a clock behind the data: returned unchanged.
 */
export function withLiveCandle<T extends ChartCandle>(
  candles: readonly T[],
  livePrice: number | undefined,
  periodSec: number,
  nowChartSec: number,
): T[] {
  const last = candles[candles.length - 1];
  if (!last || !(livePrice !== undefined && livePrice > 0 && isFinite(livePrice))) return candles as T[];
  if (nowChartSec < last.time) return candles as T[];

  const periodsAhead = Math.floor((nowChartSec - last.time) / periodSec);
  if (periodsAhead === 0) return pinLastClose(candles, livePrice, periodSec, nowChartSec);
  if (periodsAhead > MAX_LIVE_LAG_PERIODS) return candles as T[];

  const open = last.close;
  const forming = {
    ...last,
    time: last.time + periodsAhead * periodSec,
    open,
    close: livePrice,
    high: Math.max(open, livePrice),
    low: Math.min(open, livePrice),
  };
  return [...candles, forming];
}
